import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import CreateRoleModal from "./CreateRoleModal";
import { deleteRoleAssigner, getUser } from "../../../services/user.services";
import { getAdmins, getSuperAdmin } from "../../../services/admin.services";

const RoleAssigner = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isSideBarOpen = useSelector((state) => state.application.isSideBarOpen);
  const [roles, setRoles] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [edit, setEdit] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if screen is mobile
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    setError("");

    try {
      const [adminResponse, superAdminResponse] = await Promise.all([
        getAdmins(),
        getSuperAdmin(),
      ]);

      const adminRoles = Array.isArray(adminResponse.data)
        ? adminResponse.data
        : [];
      const superAdminRoles = Array.isArray(superAdminResponse.data)
        ? superAdminResponse.data
        : [];

      const combinedRoles = [...adminRoles, ...superAdminRoles];
      setRoles(combinedRoles);
    } catch (err) {
      console.error("Error fetching users:", err.message);
      setError("Failed to fetch users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const addRole = (newRole) => {
    setRoles([...roles, { ...newRole, id: roles.length + 1 }]);
    closeModal();
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const handleDelete = async (id, role) => {
    if (role === "superadmin") {
      alert("SuperAdmin role cannot be deleted.");
      return;
    }
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this role?"
    );
    if (confirmDelete) {
      try {
        await deleteRoleAssigner(id);
        setRoles(roles.filter((role) => role.id !== id));
        alert("Role deleted successfully.");
        fetchUsers();
      } catch (err) {
        console.error("Error deleting role:", err);
        setError("Failed to delete role.");
      }
    }
  };

  const handleEdit = (role) => {
    if (role.role === "superadmin") {
      alert("SuperAdmin role cannot be edited.");
      return;
    }
    setSelectedUser(role);
    setIsModalOpen(true);
  };

  // Mobile Card Component
  const MobileRoleCard = ({ role, index }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm">
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
          />
          <span className="text-sm text-gray-500">#{index + 1}</span>
        </div>
        <div className="flex space-x-2">
          <button
            className="text-blue-500 hover:text-blue-700 text-sm px-2 py-1 border border-blue-500 rounded"
            onClick={() => handleEdit(role)}
          >
            Edit
          </button>
          <button
            className={`text-sm px-2 py-1 border rounded ${
              role.role === "superadmin"
                ? "text-gray-400 border-gray-300 cursor-not-allowed"
                : "text-red-500 border-red-500 hover:text-red-700 hover:border-red-700"
            }`}
            onClick={() =>
              role.role !== "superadmin" &&
              handleDelete(role.admin_id, role.role)
            }
            disabled={role.role === "superadmin"}
          >
            Delete
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <div>
          <span className="text-xs text-gray-500 uppercase tracking-wide">
            Name
          </span>
          <p className="text-sm font-medium text-gray-900">
            {role.name || `${role.first_name} ${role.last_name}`}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="text-xs text-gray-500 uppercase tracking-wide">
              Role
            </span>
            <p className="text-sm text-gray-700">
              <span
                className={`inline-block px-2 py-1 text-xs rounded-full ${
                  role.role === "superadmin"
                    ? "bg-red-100 text-red-800"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {role.role}
              </span>
            </p>
          </div>

          <div>
            <span className="text-xs text-gray-500 uppercase tracking-wide">
              Date Created
            </span>
            <p className="text-sm text-gray-700">
              {new Date(role.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>

        <div>
          <span className="text-xs text-gray-500 uppercase tracking-wide">
            Email
          </span>
          <p className="text-sm text-gray-700 break-all">{role.email}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={`flex-1 bg-gray-100 min-h-screen transition-all duration-300 ${
        isSideBarOpen ? "ml-0 lg:ml-80" : "ml-0 md:ml-20"
      }`}
    >
      <div className="p-3 sm:p-4 lg:p-6">
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center px-4 sm:px-6 py-4 border-b border-gray-300 space-y-3 sm:space-y-0">
            <h1 className="text-lg sm:text-xl font-bold text-gray-700">
              Role Assigner
            </h1>
            <button
              onClick={openModal}
              className="w-full sm:w-auto bg-purple-600 text-white px-4 py-2 rounded-md shadow hover:bg-purple-700 transition-colors text-sm sm:text-base"
            >
              + Create a new Role
            </button>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6">
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                <p className="text-gray-600 ml-3">Loading roles...</p>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-red-600">{error}</p>
              </div>
            ) : Array.isArray(roles) && roles.length > 0 ? (
              <>
                {/* Mobile View - Cards */}
                <div className="block md:hidden">
                  {roles.map((role, index) => (
                    <MobileRoleCard key={role.id} role={role} index={index} />
                  ))}
                </div>

                {/* Desktop/Tablet View - Table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full bg-white">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-3 lg:px-6 py-3 border-b border-gray-300 text-left text-gray-600 font-medium text-xs lg:text-sm">
                          Active
                        </th>
                        <th className="px-3 lg:px-6 py-3 border-b border-gray-300 text-left text-gray-600 font-medium text-xs lg:text-sm">
                          S.No
                        </th>
                        <th className="px-3 lg:px-6 py-3 border-b border-gray-300 text-left text-gray-600 font-medium text-xs lg:text-sm">
                          Name
                        </th>
                        <th className="px-3 lg:px-6 py-3 border-b border-gray-300 text-left text-gray-600 font-medium text-xs lg:text-sm">
                          Role
                        </th>
                        <th className="px-3 lg:px-6 py-3 border-b border-gray-300 text-left text-gray-600 font-medium text-xs lg:text-sm">
                          Email ID
                        </th>
                        <th className="px-3 lg:px-6 py-3 border-b border-gray-300 text-left text-gray-600 font-medium text-xs lg:text-sm">
                          Date Created
                        </th>
                        <th className="px-3 lg:px-6 py-3 border-b border-gray-300 text-left text-gray-600 font-medium text-xs lg:text-sm">
                          Manage
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {roles.map((role, index) => (
                        <tr
                          key={role.id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-3 lg:px-6 py-4 border-b border-gray-200 text-left">
                            <input
                              type="checkbox"
                              className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                            />
                          </td>
                          <td className="px-3 lg:px-6 py-4 border-b border-gray-200 text-left text-sm">
                            {index + 1}
                          </td>
                          <td className="px-3 lg:px-6 py-4 border-b border-gray-200 text-left text-sm">
                            <div className="font-medium text-gray-900">
                              {role.name ||
                                `${role.first_name} ${role.last_name}`}
                            </div>
                          </td>
                          <td className="px-3 lg:px-6 py-4 border-b border-gray-200 text-left">
                            <span
                              className={`inline-block px-2 py-1 text-xs rounded-full font-medium ${
                                role.role === "superadmin"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-blue-100 text-blue-800"
                              }`}
                            >
                              {role.role}
                            </span>
                          </td>
                          <td className="px-3 lg:px-6 py-4 border-b border-gray-200 text-left">
                            <div
                              className="text-sm text-gray-700 max-w-xs truncate"
                              title={role.email}
                            >
                              {role.email}
                            </div>
                          </td>
                          <td className="px-3 lg:px-6 py-4 border-b border-gray-200 text-left text-sm text-gray-700">
                            {new Date(role.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-3 lg:px-6 py-4 border-b border-gray-200 text-left">
                            <div className="flex space-x-2">
                              <button
                                className="text-blue-500 hover:text-blue-700 text-sm transition-colors"
                                onClick={() => handleEdit(role)}
                              >
                                Edit
                              </button>
                              <button
                                className={`text-sm transition-colors ${
                                  role.role === "superadmin"
                                    ? "text-gray-400 cursor-not-allowed"
                                    : "text-red-500 hover:text-red-700"
                                }`}
                                onClick={() =>
                                  role.role !== "superadmin" &&
                                  handleDelete(role.admin_id, role.role)
                                }
                                disabled={role.role === "superadmin"}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 text-4xl mb-4">👥</div>
                <p className="text-gray-600">No roles found.</p>
                <button
                  onClick={openModal}
                  className="mt-4 bg-purple-600 text-white px-4 py-2 rounded-md shadow hover:bg-purple-700 transition-colors"
                >
                  Create First Role
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal Component */}
      <CreateRoleModal
        fetchUsers={fetchUsers}
        isOpen={isModalOpen}
        onClose={closeModal}
        addRole={addRole}
        user={selectedUser}
        isEditing={!!selectedUser}
      />
    </div>
  );
};

export default RoleAssigner;
